import React, { useCallback, useEffect, useState } from 'react';
import { App, Card, Drawer, Select, Space, Table, Tag, Typography } from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { csatApi, CsatListItem, CsatType } from '../api/csat';

const { Paragraph } = Typography;

const CsatPage: React.FC = () => {
  const { apiClient } = useAuth();
  const { message } = App.useApp();
  const api = useCallback(() => csatApi(apiClient!), [apiClient]);

  const [items, setItems] = useState<CsatListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [type, setType] = useState<CsatType | undefined>();
  const [sortBy, setSortBy] = useState<'Id' | 'UpdatedAt'>('Id');
  const [sortDir, setSortDir] = useState<'Asc' | 'Desc'>('Desc');
  const [detail, setDetail] = useState<CsatListItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().getList({ page, pageSize, type, sortBy, sortDir });
      setItems(res.data.items);
      setTotal(res.data.totalCount);
    } catch {
      message.error('Failed to load CSAT');
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, type, sortBy, sortDir, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleTableChange(
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CsatListItem> | SorterResult<CsatListItem>[],
  ) {
    const st = Array.isArray(sorter) ? sorter[0] : sorter;
    if (st && st.field && st.order) {
      setSortBy(st.field === 'updatedAt' ? 'UpdatedAt' : 'Id');
      setSortDir(st.order === 'ascend' ? 'Asc' : 'Desc');
    }
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setPageSize(pagination.pageSize);
  }

  const openDetail = async (id: number) => {
    try {
      const res = await api().getById(id);
      setDetail(res.data);
      setDrawerOpen(true);
    } catch {
      message.error('Failed to load CSAT detail');
    }
  };

  return (
    <Card title="CSAT">
      <Space style={{ marginBottom: 16 }}>
        <Select<CsatType | 'all'>
          style={{ width: 160 }}
          value={type ?? 'all'}
          onChange={(v) => { setType(v === 'all' ? undefined : v); setPage(1); }}
          options={[
            { value: 'all', label: 'All types' },
            { value: 'Module', label: 'Module' },
            { value: 'General', label: 'General' },
          ]}
        />
      </Space>

      <Table<CsatListItem>
        rowKey="id"
        loading={loading}
        dataSource={items}
        onChange={handleTableChange}
        onRow={(record) => ({ onClick: () => openDetail(record.id), style: { cursor: 'pointer' } })}
        pagination={{ current: page, pageSize, total, showSizeChanger: true }}
        columns={[
          {
            title: 'Id',
            dataIndex: 'id',
            width: 80,
            sorter: true,
            sortOrder: sortBy === 'Id' ? (sortDir === 'Asc' ? 'ascend' : 'descend') : null,
          },
          {
            title: 'Type',
            dataIndex: 'type',
            width: 110,
            render: (t: CsatType) => <Tag color={t === 'Module' ? 'blue' : 'green'}>{t}</Tag>,
          },
          { title: 'User', dataIndex: 'userId', width: 90 },
          {
            title: 'Module',
            dataIndex: 'moduleId',
            width: 90,
            render: (m: number | null) => m ?? '—',
          },
          {
            title: 'Rating',
            width: 90,
            render: (_: unknown, r: CsatListItem) => r.body?.rating ?? '—',
          },
          {
            title: 'Comment',
            render: (_: unknown, r: CsatListItem) =>
              r.body?.comment ? String(r.body.comment).slice(0, 80) : '—',
          },
          {
            title: 'Updated',
            dataIndex: 'updatedAt',
            width: 170,
            sorter: true,
            sortOrder: sortBy === 'UpdatedAt' ? (sortDir === 'Asc' ? 'ascend' : 'descend') : null,
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
        ]}
      />

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={520}
        title={detail ? `CSAT #${detail.id}` : 'CSAT'}
      >
        {detail && (
          <div>
            <Paragraph><b>Type:</b> {detail.type}</Paragraph>
            <Paragraph><b>User Id:</b> {detail.userId}</Paragraph>
            <Paragraph><b>Module Id:</b> {detail.moduleId ?? '—'}</Paragraph>
            <Paragraph><b>Created:</b> {dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm')}</Paragraph>
            <Paragraph><b>Updated:</b> {dayjs(detail.updatedAt).format('YYYY-MM-DD HH:mm')}</Paragraph>
            <Paragraph><b>Body:</b></Paragraph>
            <pre style={{ background: 'rgba(0,0,0,0.04)', padding: 12, borderRadius: 8 }}>
              {JSON.stringify(detail.body, null, 2)}
            </pre>
          </div>
        )}
      </Drawer>
    </Card>
  );
};

export default CsatPage;
